import { format, isThisWeek, isToday, isTomorrow } from "date-fns";
import type { Locale } from "date-fns";
import { enAU } from "date-fns/locale/en-AU";
import { enUS } from "date-fns/locale/en-US";
import { DEFAULT_LOCALE, SupportedLocale } from "~/utils/localization";

type DateFormatKey =
  | "eventDate"
  | "eventDateLong"
  | "eventCardDateTime"
  | "weekday"
  | "time";

const DATE_FNS_LOCALE = {
  "en-AU": enAU,
  "en-US": enUS,
} satisfies Record<SupportedLocale, Locale>;

const DATE_FORMATS = {
  "en-AU": {
    eventDate: "EEE d MMM",
    eventDateLong: "EEE d MMMM",
    eventCardDateTime: "EEE, d MMM, h:mma",
    weekday: "EEEE",
    time: "h:mmaaa",
  },
  "en-US": {
    eventDate: "EEE, MMM d",
    eventDateLong: "EEE, MMMM d",
    eventCardDateTime: "EEE, MMM d, h:mma",
    weekday: "EEEE",
    time: "h:mmaaa",
  },
} satisfies Record<SupportedLocale, Record<DateFormatKey, string>>;

const DATE_COPY = {
  "en-AU": {
    today: "Today",
    tomorrow: "Tomorrow",
    at: "at",
    weekStartsOn: 1,
  },
  "en-US": {
    today: "Today",
    tomorrow: "Tomorrow",
    at: "at",
    weekStartsOn: 0,
  },
} satisfies Record<
  SupportedLocale,
  { today: string; tomorrow: string; at: string; weekStartsOn: 0 | 1 }
>;

export function formatLocalizedDate(
  date: string | Date,
  key: DateFormatKey,
  locale: SupportedLocale = DEFAULT_LOCALE,
) {
  return format(date, DATE_FORMATS[locale][key], {
    locale: DATE_FNS_LOCALE[locale],
  });
}

export function formatEventDateTime(
  startDate: string | Date,
  locale: SupportedLocale = DEFAULT_LOCALE,
) {
  const time = formatLocalizedDate(startDate, "time", locale);
  const copy = DATE_COPY[locale];

  if (isToday(startDate)) {
    return `${copy.today} ${copy.at} ${time}`;
  }

  if (isTomorrow(startDate)) {
    return `${copy.tomorrow} ${copy.at} ${time}`;
  }

  if (isThisWeek(startDate, { weekStartsOn: copy.weekStartsOn })) {
    return `${formatLocalizedDate(startDate, "weekday", locale)} ${copy.at} ${time}`;
  }

  return `${formatLocalizedDate(startDate, "eventDateLong", locale)} ${copy.at} ${time}`;
}
